import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import API from 'app/api'
import { setSnackbar } from './snackbarSlice'
import { getOrgById, getAllSitesByOrgId } from './orgSlice'
import { getSite } from './siteSlice'

export const authorizeProCore = createAsyncThunk(
    'procore/authorize',
    async (_, { dispatch }) => {
        const { data } = await API.get('/procore/oauth/authorize')
        if (data?.success && data?.url) {
            window.location.href = data.url
        }
        return data
    }
)

export const getProCoreCompanies = createAsyncThunk(
    'procore/getCompanies',
    async (_, { dispatch }) => {
        try {
            const { data } = await API.get('/procore/companies')
            return data
        } catch (e) {
            dispatch(setSnackbar({ status: 'error', message: e.message }))
            throw e
        }
    }
)

export const linkCompanyToOrg = createAsyncThunk(
    'procore/linkCompany',
    async ({ org, companyId }, { dispatch }) => {
        await API.post('/procore/companies/link', { orgId: org.id, companyId })
        dispatch(getOrgById({ orgId: org.id }))
        dispatch(
            setSnackbar({
                status: 'success',
                message: 'Company linked successfully',
            })
        )
    }
)

export const getProCoreProjects = createAsyncThunk(
    'procore/getProjects',
    async ({ companyId }, { dispatch }) => {
        try {
            const { data } = await API.get(
                `/procore/companies/${companyId}/projects`
            )
            return data
        } catch (e) {
            dispatch(setSnackbar({ status: 'error', message: e.message }))
            throw e
        }
    }
)

export const linkProcoreToProject = createAsyncThunk(
    'procore/linkProject',
    async ({ projectId, procoreProjectId, companyId, orgId }, { dispatch }) => {
        try {
            await API.post('/procore/projects/link', {
                argyleProjectId: projectId,
                projectId: procoreProjectId,
                companyId,
            })
            dispatch(
                setSnackbar({
                    status: 'success',
                    message: 'Project linked successfully',
                })
            )
            dispatch(getAllSitesByOrgId({ orgId }))
        } catch (e) {
            dispatch(
                setSnackbar({
                    status: 'error',
                    message: e.response?.data?.error || e.message,
                })
            )
            throw e
        }
    }
)

export const syncProjectObservationsToProcore = createAsyncThunk(
    'procore/syncObservations',
    async ({ projectId }, { dispatch }) => {
        await API.post('/procore/observations/sync', { projectId })
        dispatch(
            setSnackbar({
                status: 'success',
                message: 'Observations synced successfully',
            })
        )
        dispatch(getSite({ siteId: projectId }))
    }
)

export const disconnectProcoreFromProject = createAsyncThunk(
    'procore/disconnectProject',
    async ({ projectId, orgId }, { dispatch }) => {
        await API.delete('/procore/projects/disconnect', {
            data: { argyleProjectId: projectId },
        })
        dispatch(
            setSnackbar({
                status: 'success',
                message: 'Procore project disconnected successfully',
            })
        )
        dispatch(getAllSitesByOrgId({ orgId }))
    }
)

export const disconnectProcoreFromOrg = createAsyncThunk(
    'procore/disconnectOrg',
    async ({ orgId, companyId }, { dispatch }) => {
        await API.delete('/procore/companies/disconnect', {
            data: { procoreCompanyId: companyId },
        })
        dispatch(
            setSnackbar({
                status: 'success',
                message: 'Procore organization disconnected successfully',
            })
        )
        await dispatch(getAllSitesByOrgId({ orgId }))
    }
)

export const disconnectProcore = createAsyncThunk(
    'procore/disconnect',
    async (_, { dispatch }) => {
        await API.delete('/procore/disconnect')
        dispatch(
            setSnackbar({
                status: 'success',
                message: 'Procore account disconnected successfully',
            })
        )
    }
)

const procoreSlice = createSlice({
    name: 'procore',
    initialState: {
        loading: false,
        data: null,
        error: null,
        companies: [],
        projects: [],
    },
    reducers: {
        clearProCoreCompanies: (state) => {
            state.companies = []
        },
        clearProCoreProjects: (state) => {
            state.projects = []
        },
        clearProCore: () => procoreSlice.getInitialState(),
        clearApiError: (state) => {
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(authorizeProCore.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(authorizeProCore.fulfilled, (state, action) => {
                state.loading = false
                state.data = action.payload
            })
            .addCase(authorizeProCore.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(getProCoreCompanies.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getProCoreCompanies.fulfilled, (state, action) => {
                state.loading = false
                state.data = action.payload
                state.companies = action.payload
            })
            .addCase(getProCoreCompanies.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(linkCompanyToOrg.pending, (state) => {
                state.loading = true
            })
            .addCase(linkCompanyToOrg.fulfilled, (state) => {
                state.loading = false
                state.companies = []
            })
            .addCase(linkCompanyToOrg.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(getProCoreProjects.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getProCoreProjects.fulfilled, (state, action) => {
                state.loading = false
                state.data = action.payload
                state.projects = action.payload
            })
            .addCase(getProCoreProjects.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(linkProcoreToProject.pending, (state) => {
                state.loading = true
            })
            .addCase(linkProcoreToProject.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(linkProcoreToProject.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(syncProjectObservationsToProcore.pending, (state) => {
                state.loading = true
            })
            .addCase(syncProjectObservationsToProcore.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(
                syncProjectObservationsToProcore.rejected,
                (state, action) => {
                    state.loading = false
                    state.error = action.error.message
                }
            )
            .addCase(disconnectProcoreFromProject.pending, (state) => {
                state.loading = true
            })
            .addCase(disconnectProcoreFromProject.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(disconnectProcoreFromProject.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(disconnectProcoreFromOrg.pending, (state) => {
                state.loading = true
            })
            .addCase(disconnectProcoreFromOrg.fulfilled, (state) => {
                state.loading = false
                state.companies = []
            })
            .addCase(disconnectProcoreFromOrg.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(disconnectProcore.pending, (state) => {
                state.loading = true
            })
            .addCase(disconnectProcore.fulfilled, () =>
                procoreSlice.getInitialState()
            )
            .addCase(disconnectProcore.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
    },
})

export const {
    clearProCoreCompanies,
    clearProCoreProjects,
    clearProCore,
    clearApiError,
} = procoreSlice.actions
export default procoreSlice.reducer
